// Accounts Receivable view models for the admin frontend. Shapes mirror the CPHub.API
// `ARController` responses (ARAccount, ARAging, ARTransaction).

export interface ARAccountPatientLink {
    id?: number;
    arAccountId?: number;
    patientId?: number;
    active?: boolean;
}

export interface ARAccountSummary {
    id: number;
    accountNumber?: string;
    responsiblePatientId?: number;
    status?: number;
    defaultCreditLimit?: number | null;
    currentBalance?: number;
    statementDeliveryMethods?: number | null;
    notes?: string | null;
    linkedPatients?: ARAccountPatientLink[] | null;
    autoPayEnabled?: boolean | null;
    autoPayStripeCardId?: string | null;
}

// A saved Stripe card returned by GET /AR/accounts/{id}/payment-methods (the responsible
// patient's payment methods), used to pick a card when enrolling an account in auto-pay.
export interface ARSavedCard {
    id: string;
    brand?: string | null;
    cardNumber?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
}

// Statement delivery channels are bit flags on the account. Print/Mail (bit 1) is intentionally
// not offered — there is no mail-vendor integration; statements deliver via email, portal, and SMS.
export const ARDeliveryMethodOptions: { value: number; text: string }[] = [
    { value: 2, text: "Email" },
    { value: 4, text: "Patient Portal Only" },
    { value: 8, text: "SMS with Portal Link" },
];

export function arDeliveryFlagsToArray(flags?: number | null): number[] {
    if (!flags) return [];
    return ARDeliveryMethodOptions.filter(o => (flags & o.value) === o.value).map(o => o.value);
}

export function arDeliveryArrayToFlags(values: number[]): number {
    return values.reduce((acc, v) => acc | v, 0);
}

export interface ARAging {
    arAccountId?: number;
    agingCurrent?: number;
    agingBucket1?: number;
    agingBucket2?: number;
    agingBucket3?: number;
    agingBucket4Plus?: number;
    total?: number;
}

export interface ARPastDueAccount {
    arAccountId?: number;
    accountNumber?: string;
    responsiblePatientId?: number;
    status?: number;
    balance?: number;
    agingCurrent?: number;
    agingBucket1?: number;
    agingBucket2?: number;
    agingBucket3?: number;
    agingBucket4Plus?: number;
    pastDueTotal?: number;
}

export interface ARStatementSummary {
    id: number;
    arAccountId?: number;
    statementDate?: string;
    periodStart?: string;
    periodEnd?: string;
    previousBalance?: number;
    periodCharges?: number;
    periodCredits?: number;
    endingBalance?: number;
    dueByDate?: string;
    status?: number;
}

// Result of GET /AR/accounts/{id}/credit-check?amount=. Drives whether AR may be elected as a
// tender at checkout and whether an over-limit override is required.
export interface ARCreditCheck {
    arAccountId?: number;
    status?: number;
    creditLimit?: number | null;
    balance?: number;
    availableCredit?: number | null;
    projectedBalance?: number;
    overLimit: boolean;
    allowed: boolean;
}

export interface ARPaymentView {
    id: number;
    arAccountId?: number;
    arTransactionId?: number;
    amount?: number;
    paymentMethod?: number;
    checkNumber?: string | null;
    referenceId?: string | null;
    status?: number;
    reasonCode?: string | null;
    notes?: string | null;
    created?: string;
}

export const ARPaymentMethodOptions: { value: number; text: string }[] = [
    { value: 1, text: "Cash" },
    { value: 2, text: "Check" },
    { value: 3, text: "Credit Card" },
];

// Manual transaction types a user may post (system types are excluded).
export const ARAdjustmentTypeOptions: { value: number; text: string }[] = [
    { value: 4, text: "Manual Adjustment" },
    { value: 5, text: "Courtesy Credit" },
    { value: 7, text: "Balance Forward" },
    { value: 8, text: "Opening Balance" },
];

export function arPaymentMethodLabel(method?: number): string {
    return ARPaymentMethodOptions.find(o => o.value === method)?.text ?? "";
}

export function arPaymentStatusLabel(status?: number): string {
    return status === 2 ? "Voided" : status === 1 ? "Posted" : "";
}

export interface ARLedgerEntry {
    id: number;
    arAccountId?: number;
    orderId?: number | null;
    transactionType?: number;
    amount?: number;
    reasonCode?: string | null;
    isSystemGenerated?: boolean;
    notes?: string | null;
    created?: string;
}

const accountStatusLabels: Record<number, string> = {
    1: "Active",
    2: "On Hold",
    3: "Closed",
    4: "Written Off",
};

const transactionTypeLabels: Record<number, string> = {
    1: "Order Charge",
    2: "Insurance Adjustment",
    3: "Reversal Credit",
    4: "Manual Adjustment",
    5: "Courtesy Credit",
    6: "Write-Off",
    7: "Balance Forward",
    8: "Opening Balance",
    9: "Payment",
    10: "Refund",
};

export function arAccountStatusLabel(status?: number): string {
    return status ? accountStatusLabels[status] ?? "Unknown" : "";
}

export function arTransactionTypeLabel(type?: number): string {
    return type ? transactionTypeLabels[type] ?? "Unknown" : "";
}
